import { useEffect, useState, useRef, useCallback } from "react";
import "./clock-picker.css";

const pad = (n) => String(n).padStart(2, "0");

export default function ClockPicker({ clockTime, setClockTime, onFocus }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("hour");
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);

  useEffect(() => {
    if (!open || !clockTime) return;
    const [h, m] = clockTime.split(":").map(Number);
    setHour(h);
    setMinute(m);
    setMode("hour");
  }, [open, clockTime]);

  const confirm = () => {
    setClockTime(`${pad(hour)}:${pad(minute)}`);
    setOpen(false);
  };

  return (
    <>
      <input
        className="clock"
        placeholder="00:00"
        readOnly
        value={clockTime}
        onClick={() => {
          setOpen(true);
          onFocus?.();
        }}
      />

      {open && (
        <div className="clock-overlay" onMouseDown={() => setOpen(false)}>
          <div className="clock-panel" onMouseDown={(e) => e.stopPropagation()}>
            <div className="clock-header">
              <span
                className={mode === "hour" ? "active" : ""}
                onClick={() => setMode("hour")}
              >
                {pad(hour)}
              </span>
              <span className="separator">:</span>
              <span
                className={mode === "minute" ? "active" : ""}
                onClick={() => setMode("minute")}
              >
                {pad(minute)}
              </span>
            </div>

            <div className="clock-body">
              <ClockDial
                max={mode === "hour" ? 24 : 60}
                step={1}
                labelStep={mode === "hour" ? 1 : 5}
                value={mode === "hour" ? hour : minute}
                onChange={(v) => {
                  if (mode === "hour") setHour(v);
                  else setMinute(v);
                }}
                // onMouseUp={() => {
                //   if (mode === "hour") setMode("minute");
                // }}
              />
            </div>

            <div className="clock-actions">
              <button onClick={() => setOpen(false)}>取消</button>
              <button className="ok" onClick={confirm}>
                確認
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ClockDial({ max, step, labelStep, value, onChange, onMouseUp }) {
  const dialRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // 核心數學邏輯：根據座標計算數值
  const calculateValue = useCallback(
    (clientX, clientY) => {
      if (!dialRef.current) return;
      const rect = dialRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const x = clientX - centerX;
      const y = clientY - centerY;

      // atan2 回傳弧度，轉成角度 (0-360)
      let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
      if (angle < 0) angle += 360;

      // 根據總份數 (max) 計算對應值
      let newValue = Math.round((angle / 360) * max);
      if (newValue === max) newValue = 0;

      onChange(newValue);
    },
    [max, onChange],
  );

  const handleMouseDown = (e) => {
    setIsDragging(true);
    calculateValue(e.clientX, e.clientY);
  };

  useEffect(() => {
    const handleGlobalMove = (e) => {
      if (!isDragging) return;
      // 支援觸控與滑鼠
      const x = e.clientX || (e.touches && e.touches[0].clientX);
      const y = e.clientY || (e.touches && e.touches[0].clientY);
      calculateValue(x, y);
    };

    const handleGlobalUp = () => {
      if (isDragging) {
        setIsDragging(false);
        onMouseUp?.();
      }
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleGlobalMove);
      window.addEventListener("mouseup", handleGlobalUp);
      window.addEventListener("touchmove", handleGlobalMove, {
        passive: false,
      });
      window.addEventListener("touchend", handleGlobalUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleGlobalMove);
      window.removeEventListener("mouseup", handleGlobalUp);
      window.removeEventListener("touchmove", handleGlobalMove);
      window.removeEventListener("touchend", handleGlobalUp);
    };
  }, [isDragging, calculateValue, onMouseUp]);

  // 渲染刻度 (邏輯同前，但移除個別點擊，統一由盤面處理)
  const items = [];
  const center = 140;
  const r = max === 24 ? 105 : 112;

  for (let i = 0; i < max; i++) {
    const angle = (360 / max) * i - 90;
    const rad = (angle * Math.PI) / 180;
    const isLabel = i % labelStep === 0;
    items.push(
      <div
        key={i}
        className={`clock-item ${i === value ? "selected" : ""} ${!isLabel ? "dot" : ""}`}
        style={{
          left: `${center + r * Math.cos(rad)}px`,
          top: `${center + r * Math.sin(rad)}px`,
        }}
      >
        {isLabel ? pad(i) : ""}
      </div>,
    );
  }

  return (
    <div
      className={`clock-dial ${isDragging ? "dragging" : ""}`}
      ref={dialRef}
      onMouseDown={handleMouseDown}
      onTouchStart={(e) => {
        setIsDragging(true);
        calculateValue(e.touches[0].clientX, e.touches[0].clientY);
      }}
    >
      <div className="clock-center" />
      <div
        className="clock-hand"
        style={{
          transform: `translateX(-50%) rotate(${(360 / max) * value}deg)`,
        }}
      >
        <div className="clock-hand-head" />
      </div>
      {items}
    </div>
  );
}
